import Component from '@glimmer/component'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { service } from '@ember/service'
import { trackedFunction } from 'reactiveweb/function'
import { format } from 'date-fns'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Store from '@ember-data/store'
import type { IntlService } from 'ember-intl'
import type School from 'teacher-ui-client/models/school'
import type Teacher from 'teacher-ui-client/models/teacher'
import type SubscriptionUtilisation from 'teacher-ui-client/models/subscription-utilisation'
import { HomeTile } from 'teacher-ui-client/components/home/tile'
import { t } from 'ember-intl'
import { UiButtonLink } from 'teacher-ui-client/components/ui-button/link'
import { UiButtonLinkTo } from 'teacher-ui-client/components/ui-button/link-to'
import { LinkTo } from '@ember/routing'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type SchoolTrialCount from 'teacher-ui-client/models/school-trial-count'
import config from 'teacher-ui-client/config/environment'
import { join } from 'teacher-ui-client/utils/uri'
import { canAccess } from 'teacher-ui-client/helpers/can-access'

const oneRosterUrl = join(config.oneRosterUrl, 'login').toString().concat('?return_to=/manage-subscriptions')

interface Args {
  school?: School
  subscriptionType: SubscriptionType
  utilisation?: SubscriptionUtilisation
  isSubco?: boolean
  subcoForSubscription?: Teacher
}

export class ManageSubscription extends Component<Args> {
  @service store!: Store

  @service intl!: IntlService

  /**
   * (school sub) -> Your {Reading Eggs/Mathseeds/Writing Legends} subscription expires on {date}
   * (trial sub) -> Your {Reading Eggs/Mathseeds/Writing Legends} trial expires on {date}
   */
  get subscriptionExpiryText() {
    const isTrialSub = Boolean(this.args.utilisation?.teacherSubscription) // if utilisation is teacher sub, its a trial

    const subscriptionName = this.intl.t(`subscriptions.${this.args.subscriptionType}`)
    return isTrialSub
      ? this.intl.t('pages.home.panels.manageSubscription.trialSubExpiry', { subscriptionName })
      : this.intl.t('pages.home.panels.manageSubscription.schoolSubExpiry', { subscriptionName })
  }

  get subscriptionEndDate() {
    const { utilisation } = this.args
    const date = utilisation?.teacherSubscription?.endDate ?? utilisation?.schoolSubscription?.endDate
    if (date) return format(date, this.intl.t('dateFormats.dateFns.MMMM dd, yyyy'))
    return '-'
  }

  fetchSchoolTrialCount = trackedFunction(this, async () => {
    const id = `${this.args.school?.id}_${this.args.subscriptionType}`
    const adapterOptions = { schoolId: this.args.school?.id, subscriptionType: this.args.subscriptionType }

    await Promise.resolve() // disconnect tracking before store calls

    return await this.store.findRecord<SchoolTrialCount>('school-trial-count', id, { adapterOptions })
  }); /* prettier-ignore */ /* prettier-ignore-end */

  <template>
    <HomeTile
      @themeKey={{ThemeKeys.orange}}
      @title={{t "pages.home.panels.manageSubscription.title"}}
      @alert={{@utilisation.isOverCapacity}}
    >
      <div class="bg-white p-3">
        <p class="mb-3" data-test-subscription-expiry>
          <span>{{this.subscriptionExpiryText}}</span>
          <strong class="mt-3 block">{{this.subscriptionEndDate}}</strong>
        </p>
        <p class="mb-3" data-test-subscription-counts>
          {{t "pages.home.panels.manageSubscription.activeStudents"}}
          {{#if @utilisation.isOverCapacity}}
            <strong class="text-medal-bronze-400">{{@utilisation.studentCount}}
              /
              {{@utilisation.capacitySize}}
              —
              {{t "pages.home.panels.manageSubscription.studentsExceeded"}}
            </strong>
          {{else}}
            <strong>{{@utilisation.studentCount}} / {{@utilisation.capacitySize}}</strong>
          {{/if}}
        </p>
        {{#if @utilisation.isOverCapacity}}
          {{#if @isSubco}}
            <p class="mb-3" data-test-subscription-over-capacity-message>
              <LinkTo @route="subscription-type.contact-us">
                {{t "pages.home.panels.manageSubscription.contactSupport"}}
              </LinkTo>
              <span>
                {{t "pages.home.panels.manageSubscription.isSubco.postContact"}}
              </span>
            </p>
          {{else if @subcoForSubscription}}
            <p class="mb-3" data-test-subscription-over-capacity-message>
              {{t "pages.home.panels.manageSubscription.hasSubco.subscriptionHelp.contactYourSub"}}
            </p>
          {{else}}
            <p class="mb-3" data-test-subscription-over-capacity-message>
              <LinkTo @route="subscription-type.contact-us">
                {{t "pages.home.panels.manageSubscription.contactSupport"}}
              </LinkTo>
              <span>
                {{t "pages.home.panels.manageSubscription.missingSubco.subscriptionHelp.postContactSupport"}}
              </span>
            </p>
          {{/if}}
        {{/if}}
        {{#if @isSubco}}
          {{#if this.fetchSchoolTrialCount.isPending}}
            <p class="mb-3" data-test-subscription-trial-counts>
              {{t "pages.home.panels.manageSubscription.isSubco.activeTrials"}}
              {{t "resourceCounts.teachers" count="-"}},
              {{t "resourceCounts.students" count="-"}}
            </p>
          {{else if this.fetchSchoolTrialCount.value}}
            <p class="mb-3" data-test-subscription-trial-counts>
              {{t "pages.home.panels.manageSubscription.isSubco.activeTrials"}}
              {{t "resourceCounts.teachers" count=this.fetchSchoolTrialCount.value.teacherTrialCount}},
              {{t "resourceCounts.students" count=this.fetchSchoolTrialCount.value.studentTrialCount}}
            </p>
          {{/if}}
          <div class="mt-3 text-center" data-test-is-subco>
            {{#if (canAccess "one_roster")}}
              <UiButtonLink
                class="whitespace-nowrap"
                data-test-manage-subscription-link
                href={{oneRosterUrl}}
                target="_blank"
                rel="noopener noreferrer"
                @appearance={{ButtonAppearance.regular}}
              >
                <span>{{t "manage.3essentials.marketingTile.goToSubscriptions"}}</span>
                <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
              </UiButtonLink>
            {{else}}
              <UiButtonLinkTo
                data-test-manage-subscription-link
                @route="manage.subscription"
                @appearance={{ButtonAppearance.orange}}
              >
                {{t "pages.home.panels.manageSubscription.isSubco.buttonLabel"}}
              </UiButtonLinkTo>
            {{/if}}
          </div>
        {{else if @subcoForSubscription}}
          <hr class="border-dusty-black-100 my-3" />
          <div data-test-has-subco>
            <p>
              {{t "pages.home.panels.manageSubscription.hasSubco.yourSubcoIs"}}
              <strong class="whitespace-nowrap">{{@subcoForSubscription.fullName}}</strong>
            </p>
            <a class="whitespace-nowrap" href="mailto:{{@subcoForSubscription.email}}">
              <PhosphorIcon @icon="phosphor-envelope-simple-fill" />
              {{t "pages.home.panels.manageSubscription.hasSubco.emailSubco"}}
            </a>
          </div>

        {{else}}
          <hr class="border-dusty-black-100 my-3" />
          <div data-test-missing-subco>
            <p>
              {{t
                "pages.home.panels.manageSubscription.missingSubco.noSubco"
                subscriptionType=(t @subscriptionType)
                htmlSafe=true
              }}
            </p>
            <p data-test-signup-as-coordinator>
              {{t "pages.home.panels.manageSubscription.missingSubco.signupAsACoordinator"}}
            </p>
            <LinkTo @route="subscription-type.account" class="whitespace-nowrap">
              <PhosphorIcon @icon="phosphor-user-gear-fill" />
              {{t "pages.home.panels.manageSubscription.missingSubco.signUpAsSubcoLink"}}
            </LinkTo>
          </div>

        {{/if}}
      </div>
    </HomeTile>
  </template>
}

export default ManageSubscription
